<template>
  <div class="space-y-5">
    <section class="acc-glass rounded-2xl border border-white/10 p-4">
      <div class="text-lg font-extrabold tracking-tight">Цитати й факти</div>
      <div class="mt-1 text-sm text-white/65">Цитати й факти про книги, збережені у профілі.</div>

      <div class="mt-4 grid grid-cols-3 gap-2 sm:max-w-lg">
        <div v-for="summary in summaries" :key="summary.key"
          class="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs">
          <div class="text-white/60">{{ summary.label }}</div>
          <div class="text-base font-extrabold">{{ summary.value }}</div>
        </div>
      </div>

      <div class="mt-4 inline-flex max-w-full rounded-xl border border-white/10 bg-white/5 p-1" role="tablist"
        aria-label="Тип внесків">
        <button v-for="option in typeOptions" :key="option.value" type="button" role="tab"
          class="rounded-lg px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/40 sm:px-4"
          :class="activeType === option.value ? 'bg-foxy-action text-white shadow-sm' : 'text-white/65 hover:text-white'"
          :aria-selected="activeType === option.value ? 'true' : 'false'" @click="selectType(option.value)">
          {{ option.label }}
        </button>
      </div>
    </section>

    <section v-if="counts.total > 0 || searchQuery" class="acc-glass rounded-2xl border border-white/10 p-4">
      <form class="grid grid-cols-1 gap-2 md:grid-cols-3" @submit.prevent="runSearchNow">
        <label class="md:col-span-2">
          <span class="sr-only">Пошук за назвою книги</span>
          <input v-model="searchQuery" type="search" class="foxy-input !px-3 !py-2 text-sm"
            placeholder="Пошук за назвою книги...">
        </label>
        <label>
          <span class="sr-only">Сортування внесків</span>
          <select v-model="sort" class="foxy-select !px-3 !py-2 text-sm">
            <option value="newest">Новіші</option>
            <option value="oldest">Старіші</option>
          </select>
        </label>
      </form>
      <div v-if="searchQuery.trim()" class="mt-2 text-xs text-white/60" role="status" aria-live="polite">
        <span v-if="loading">Шукаємо…</span>
        <span v-else>Знайдено: {{ matchingTotal }}</span>
      </div>
    </section>

    <div v-if="notice.message" class="acc-glass rounded-2xl border p-3 text-sm"
      :class="notice.type === 'error' ? 'border-red-400/30 text-red-200' : 'border-emerald-400/30 text-emerald-200'">
      {{ notice.message }}
    </div>

    <div v-if="loadError && !items.length" class="acc-glass rounded-2xl border border-red-400/30 p-4 text-sm text-red-200">
      {{ loadError }}
      <button type="button" class="acc-btn ml-2 !px-3 !py-1.5 text-xs" @click="fetchItems(1)">Спробувати ще</button>
    </div>
    <div v-else-if="loading && !items.length" class="acc-glass rounded-2xl border border-white/10 p-4 text-sm text-white/65">
      Завантаження цитат і фактів…
    </div>
    <div v-else-if="isTrueEmpty" class="acc-glass rounded-2xl border border-white/10 p-4 text-sm text-white/65">
      {{ trueEmptyCopy }}
    </div>
    <div v-else-if="isSearchEmpty" class="acc-glass rounded-2xl border border-white/10 p-4 text-sm text-white/65">
      За цим пошуком нічого не знайдено.
    </div>

    <div v-else class="space-y-3">
      <template v-for="item in items">
        <AccountQuoteCard v-if="item.type === 'quote'" :key="item.key" :quote="item"
           :publication-context="publicationContext"
          @updated="handleUpdated('quote', $event)" @deleted="(id, count, context) => handleDeleted('quote', id, count, context)" @notice="showNotice"
          @publication-mutation-start="$emit('publication-mutation-start', $event)"
          @publication-mutation-finish="$emit('publication-mutation-finish', $event)" />
        <AccountFactCard v-else-if="item.type === 'fact'" :key="item.key" :fact="item"
           :publication-context="publicationContext"
          @updated="handleUpdated('fact', $event)" @deleted="(id, count, context) => handleDeleted('fact', id, count, context)" @notice="showNotice"
          @publication-mutation-start="$emit('publication-mutation-start', $event)"
          @publication-mutation-finish="$emit('publication-mutation-finish', $event)" />
      </template>

      <div v-if="loadError" class="text-sm text-red-200">{{ loadError }}</div>
      <div v-if="hasMore" class="flex justify-center pt-2">
        <button type="button" class="acc-btn-primary" :disabled="loadingMore" @click="loadMore">
          {{ loadingMore ? 'Завантаження…' : 'Показати ще' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import axios from 'axios';
import AccountFactCard from '../components/AccountFactCard.vue';
import AccountQuoteCard from '../components/AccountQuoteCard.vue';

const VALID_TYPES = ['all', 'quotes', 'facts'];

export default {
  name: 'ProfileQuotesFacts',
  components: { AccountFactCard, AccountQuoteCard },
  props: {
    profile: { type: Object, required: true },
    publicationContext: { type: Object, default: null },
  },
  data() {
    return {
      items: [],
      counts: { total: 0, quotes: 0, facts: 0 },
      matchingTotal: 0,
      searchQuery: '',
      sort: 'newest',
      currentPage: 0,
      lastPage: 1,
      loading: false,
      loadingMore: false,
      searchPending: false,
      loadError: '',
      debounceTimer: null,
      requestSequence: 0,
      notice: { message: '', type: 'success' },
      noticeTimer: null,
      typeOptions: [
        { value: 'all', label: 'Усі' },
        { value: 'quotes', label: 'Цитати' },
        { value: 'facts', label: 'Факти' },
      ],
    };
  },
  computed: {
    requestContext() { return JSON.stringify([this.profile.username, this.activeType, this.searchQuery.trim(), this.sort]); },
    activeType() {
      const requested = String(this.$route.query.type || 'all');
      return VALID_TYPES.includes(requested) ? requested : 'all';
    },
    summaries() {
      return [
        { key: 'total', label: 'Усього', value: Number(this.counts.total || 0) },
        { key: 'quotes', label: 'Цитат', value: Number(this.counts.quotes || 0) },
        { key: 'facts', label: 'Фактів', value: Number(this.counts.facts || 0) },
      ];
    },
    selectedCount() {
      return this.activeType === 'all' ? this.counts.total : this.counts[this.activeType];
    },
    hasMore() {
      return this.currentPage < this.lastPage;
    },
    isTrueEmpty() {
      return !this.loading && !this.searchQuery.trim() && Number(this.selectedCount || 0) === 0;
    },
    isSearchEmpty() {
      return !this.loading && Boolean(this.searchQuery.trim()) && this.matchingTotal === 0;
    },
    trueEmptyCopy() {
      if (this.activeType === 'quotes') return 'У профілі поки немає цитат.';
      if (this.activeType === 'facts') return 'У профілі поки немає фактів.';
      return 'У профілі поки немає цитат і фактів.';
    },
  },
  watch: {
    searchQuery() {
      this.scheduleReset();
    },
    sort() {
      this.scheduleReset(0);
    },
    activeType() {
      this.scheduleReset(0);
    },
  },
  mounted() {
    this.fetchItems(1);
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
    clearTimeout(this.noticeTimer);
    this.requestSequence += 1;
  },
  methods: {
    selectType(type) {
      if (type === this.activeType) return;
      if (type === 'quotes' || type === 'facts') {
        const { type: previousType, ...query } = this.$route.query;
        return this.$router.push({ name: type === 'quotes' ? 'acc.profile.quotes' : 'acc.profile.facts', params: this.$route.params, query });
      }
      const query = { ...this.$route.query };
      if (type === 'all') delete query.type;
      else query.type = type;
      this.$router.push({ name: 'acc.profile.quotes-facts', params: this.$route.params, query });
    },
    invalidateContinuation() {
      this.requestSequence += 1;
      this.currentPage = 0;
      this.lastPage = 1;
      this.loadingMore = false;
      this.searchPending = true;
    },
    scheduleReset(delay = 275) {
      clearTimeout(this.debounceTimer);
      this.invalidateContinuation();
      this.debounceTimer = setTimeout(() => this.fetchItems(1), delay);
    },
    runSearchNow() {
      clearTimeout(this.debounceTimer);
      this.invalidateContinuation();
      return this.fetchItems(1);
    },
    async fetchItems(page) {
      const requestId = ++this.requestSequence;
      const context = this.requestContext;
      const isFirstPage = page === 1;
      this.loadError = '';
      if (isFirstPage) this.loading = true;
      else this.loadingMore = true;

      try {
        const response = await axios.get(`/api/account/profile/${this.profile.username}/quotes-facts`, {
          params: {
            page,
            type: this.activeType,
            sort: this.sort,
            q: this.searchQuery.trim() || undefined,
          },
        });
        if (requestId !== this.requestSequence || context !== this.requestContext || this._isDestroyed) return;

        const rows = response.data.data || [];
        const merged = isFirstPage ? rows : [...this.items, ...rows];
        this.items = Array.from(new Map(merged.map(item => [item.key || item.type + ':' + item.id, item])).values());
        this.counts = response.data.counts || { total: 0, quotes: 0, facts: 0 };
        this.currentPage = Number(response.data.meta?.current_page || page);
        this.lastPage = Number(response.data.meta?.last_page || 1);
        this.matchingTotal = Number(response.data.meta?.total || 0);
      } catch (error) {
        if (requestId !== this.requestSequence || context !== this.requestContext || this._isDestroyed) return;
        this.loadError = error.response?.data?.message || 'Не вдалося завантажити цитати й факти.';
      } finally {
        if (requestId === this.requestSequence && context === this.requestContext && !this._isDestroyed) {
          this.searchPending = false;
          this.loading = false;
          this.loadingMore = false;
        }
      }
    },
    loadMore() {
      if (!this.loading && !this.loadingMore && !this.searchPending && this.hasMore) return this.fetchItems(this.currentPage + 1);
    },
    handleUpdated(type, updatedItem) {
      const index = this.items.findIndex((item) => item.type === type && item.id === updatedItem.id);
      if (index !== -1) this.$set(this.items, index, { ...this.items[index], ...updatedItem });
      this.invalidateContinuation();
      return this.fetchItems(1);
    },
    handleDeleted(type, itemId, count, context) {
      if (this._isDestroyed || !context || context.username !== this.profile.username
        || context.generation !== this.publicationContext?.generation) return;
      this.items = this.items.filter((item) => !(item.type === type && item.id === itemId));
      this.$emit(`${type}-deleted`, { ...context, [type === 'quote' ? 'quotesCount' : 'factsCount']: count });
      this.invalidateContinuation();
      return this.fetchItems(1);
    },
    showNotice(message, type = 'success') {
      this.notice = { message, type };
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => {
        this.notice = { message: '', type: 'success' };
      }, 4000);
    },
  },
};
</script>
